import React from 'react'

import { Footer } from '../Footer'

import {
  Wrapper,
  Main,
  FooterWrapper,
  HeaderContainer,
} from './IndexLayout.styles'

import { IndexLayoutHeader, IndexLayoutHeaderProps } from './IndexLayoutHeader'
import {
  createLayoutPortal,
  useCreateLayoutHost,
  useLayoutOverrideProps,
  workspaceSidebarLayoutSymbols,
} from '@sketch/utils'

export interface LayoutPassDownProps
  extends Pick<
    IndexLayoutProps,
    'noHeaderBorder' | 'maxContainerWidth' | 'showFooter' | 'disableLogoLink'
  > {}

export interface IndexLayoutExtraProps {
  HeaderPortal: React.FunctionComponent<React.PropsWithChildren<{}>>
  useOverrideLayoutProps: (props: LayoutPassDownProps) => void
  OverrideLayoutProps: (props: LayoutPassDownProps) => JSX.Element
}

interface RenderPropChildren {
  render: (props: IndexLayoutExtraProps) => React.ReactElement
}

interface ComponentChildren {
  component: React.ComponentType<IndexLayoutExtraProps>
}

export type IndexLayoutProps = Pick<IndexLayoutHeaderProps, 'disableLogoLink'> &
  OneOf<RenderPropChildren, ComponentChildren> & {
    className?: string
    fullWidth?: boolean
    maxContainerWidth?: string
    showFooter?: boolean
    noHeaderBorder?: boolean
  }

const HeaderPortal = createLayoutPortal(workspaceSidebarLayoutSymbols.header)

export const IndexLayout: React.FC<IndexLayoutProps> = props => {
  const ref = useCreateLayoutHost(workspaceSidebarLayoutSymbols.header)
  const { getOverriddenProps, useOverrideLayoutProps, OverrideLayoutProps } =
    useLayoutOverrideProps<LayoutPassDownProps>()

  const childrenProps = {
    HeaderPortal,
    useOverrideLayoutProps,
    OverrideLayoutProps,
  }

  const {
    className,
    disableLogoLink,
    showFooter = true,
    render,
    component: Component,
  }: typeof props = getOverriddenProps(props)

  return (
    <Wrapper className={className}>
      <IndexLayoutHeader disableLogoLink={disableLogoLink}>
        <HeaderContainer ref={ref} />
      </IndexLayoutHeader>
      <Main>
        {render && render(childrenProps)}
        {Component && <Component {...childrenProps} />}
      </Main>
      {showFooter && (
        <FooterWrapper>
          <Footer />
        </FooterWrapper>
      )}
    </Wrapper>
  )
}
